/**
 * OAuth 相关的工具函数
 */
// 导入语言检测工具
import { getAppProtocol } from '@/shared/constance/appConfig';
import { isChinese } from '@/utils/languageUtils';

export interface OAuthParams {
  client_id?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  redirect_uri?: string;
}

/**
 * 从 URL 读取查询参数（仅允许安全的参数）
 */
export function getUrlParams(): Record<string, string> {
  const urlParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};

  // 白名单：只允许这些安全的参数
  const allowedParams = ['port', 'code', 'error', 'referrer'];

  for (const [key, value] of urlParams.entries()) {
    if (allowedParams.includes(key)) {
      // 对参数值进行基本的清理，移除潜在的危险字符
      const cleanValue = value.replace(/[<>"'&]/g, '');
      params[key] = cleanValue;
    }
  }

  return params;
}
// 根据语言环境和 referrer 返回不同的配置常量
export const OAUTH_CONFIG = (referrer?: string | null) => {
  const protocol = getAppProtocol(referrer);
  const redirectUri = `${protocol}streamlake.codeflicker/vercelToken`;

  return {
    VERCEL_REDIRECT_URI_CODEFLICKER: redirectUri,
    VERCEL_CLIENT_ID_OUTER: 'oac_fQivH60DlcBayzLHLQTocLt6', // 暂时使用相同的
  } as const;
};

/**
 * 构建 Vercel OAuth iframe src URL
 */
export async function buildVercelOAuthUrl(): Promise<string> {
  const baseUrl = 'https://vercel.com/integrations/codeflicker/new';

  // 获取当前页面的 URL 作为 redirect_uri
  const currentUrl = window.location.origin + window.location.pathname;

  const { codeVerifier, codeChallenge } = await generateCodeChallenge();
  // 合并参数：URL参数 > 自定义参数 > 默认参数
  const params: OAuthParams = {
    client_id: OAUTH_CONFIG().VERCEL_CLIENT_ID_OUTER,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    redirect_uri: currentUrl,
    state: generateState(),
  };

  // 过滤掉空值
  const filteredParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== '')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const queryParams = new URLSearchParams(filteredParams as Record<string, string>);

  return Promise.resolve(`${baseUrl}?${queryParams.toString()}`);
}

/**
 * 生成随机的 state 参数
 */
export function generateState(): string {
  return crypto.randomUUID();
}

/**
 * 生成 PKCE code challenge
 */
export async function generateCodeChallenge(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  // 生成 code verifier
  const codeVerifier = generateRandomString(32);

  // 生成 code challenge
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = base64URLEncode(digest);

  return { codeVerifier, codeChallenge };
}

/**
 * 生成随机字符串
 */
function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);

  for (let i = 0; i < length; i++) {
    result += charset[values[i] % charset.length];
  }

  return result;
}

/**
 * Base64 URL 编码
 */
function base64URLEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * 验证必需的 OAuth 参数
 */
export function validateOAuthParams(params: Record<string, string>): {
  isValid: boolean;
  missingParams: string[];
} {
  const requiredParams = ['client_id', 'state', 'code_challenge'];
  const missingParams = requiredParams.filter((param) => !params[param]);

  return {
    isValid: missingParams.length === 0,
    missingParams,
  };
}
